import { browser } from '$app/environment';
import { publicAuthRoutes } from './routing-utils';
import {
  swUpdateAvailable,
  swRegistration,
} from './stores/service-worker-store';

let isUpdating = false; // Flag to track if we're currently updating
const REFRESH_SERVICE_WORKER_INTERVAL = 60 * 1000 * 5; // 5 minutes
const DEBUG = false; // Set to true to enable verbose logging

function log(...args: unknown[]): void {
  if (DEBUG) console.log('[ServiceWorker Client]', ...args);
}

function logError(...args: unknown[]): void {
  console.error('[ServiceWorker Client]', ...args);
}

// Register service worker and set up message handling
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!browser || !('serviceWorker' in navigator)) return null;

  try {
    // Check 1: Setup a listener for messages from the service worker about a version change
    // It pings the server on an interval to see if there is a new version available.
    // This is primarily a fallback in case the service worker fails to update itself.
    // Most of the time, check 2 will handle the update.
    setupMessageListener();

    // Then register the service worker
    const registration =
      await navigator.serviceWorker.register('/service-worker.js');
    swRegistration.set(registration);
    log('Service worker registered successfully');

    // Check 2: Check if there's already a waiting service worker
    // This handles the case where a new service worker has been deployed and installed,
    // but is waiting to activate. This occurs when:
    // 1. User visits with service worker version A
    // 2. New version B is deployed
    // 3. User navigates away or closes the site
    // 4. User returns to the site, finding version B waiting to activate
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      newWorker?.addEventListener('statechange', () => {
        if (newWorker.state === 'installed' && registration.waiting) {
          log('New service worker is waiting, update available');
          handleAvailableUpdate();
        }
      });
    });

    // Periodically check for service worker updates
    // This is to ensure that the service worker is up to date
    // and to prevent the service worker from becoming stale
    setInterval(() => {
      registration
        .update()
        .then(() => {
          log('Checked for service worker updates');
        })
        .catch((err) => {
          logError('Error checking for service worker updates:', err);
        });
    }, REFRESH_SERVICE_WORKER_INTERVAL);

    return registration;
  } catch (error) {
    logError('Service worker registration failed:', error);
    return null;
  }
}

/**
 * Handles when a new service worker update is available.
 * For auth pages, forces an immediate reload to ensure the client is updated.
 * For other pages, shows an update notification that the user can act on.
 */
function handleAvailableUpdate(): void {
  const pathname = window.location.pathname;
  if (publicAuthRoutes.has(pathname)) {
    window.location.reload();
  } else {
    swUpdateAvailable.set(true);
  }
}

// Set up message listener to receive update notifications from service worker
function setupMessageListener(): void {
  if (!browser || !('serviceWorker' in navigator)) return;

  // Listen for messages from any service worker
  navigator.serviceWorker.addEventListener('message', (event) => {
    const message = event.data;
    log('Message received:', message);

    if (message?.type === 'VERSION_UPDATE' && !isUpdating) {
      log('Update available:', message);
      handleAvailableUpdate();
    } else if (message?.type === 'SKIP_WAITING_DONE') {
      log('Service worker has completed skipWaiting, reloading page');

      if (browser && typeof window !== 'undefined') {
        log('Reloading page to apply update');
        window.location.reload();
      }
    }
  });
}

// Apply the update when requested by the user
export function applyUpdate(): void {
  if (!browser || !('serviceWorker' in navigator)) return;

  log('User requested update application');

  // Set updating flag to prevent new notifications
  isUpdating = true;
  swUpdateAvailable.set(false);

  swRegistration.subscribe((registration) => {
    if (!registration) {
      logError('No service worker registration found');
      isUpdating = false;
      return;
    }

    if (registration.waiting) {
      log('Sending SKIP_WAITING message to waiting service worker');
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    } else {
      log('No waiting service worker found');
      isUpdating = false;
      swUpdateAvailable.set(true); // Restore the update notification since we don't have a waiting service worker
    }
  })();
}
